export type PrintProviderKind = "QZ" | "BROWSER";
export type PaperWidth = "58mm" | "80mm";

/** Delivery evidence only. None of these statuses means paper exited. */
export type PrintEvidenceStatus =
  | "SPOOLED"
  | "FALLBACK_DIALOG_CLOSED"
  | "UNKNOWN";

export interface PrintEvidence {
  provider: PrintProviderKind;
  status: PrintEvidenceStatus;
  observedAt: string;
  /** Stable, payload-free class suitable for audit and support UI. */
  errorClass?: string;
}
export interface TestedPrinterProfile {
  /** Human/config identifier for the exact printer + driver + OS profile. */
  id: string;
  paperWidths: readonly PaperWidth[];
  pixelHtml: boolean;
  /** Base64 commands exist only for named, real-device-qualified profiles. */
  rawCommands?: {
    cutBase64?: string;
    cashDrawerBase64?: string;
  };
}

export interface PrintHardwareActions {
  /** Both actions are off unless explicitly requested for a capable profile. */
  cut?: boolean;
  openCashDrawer?: boolean;
}

/** Identifies the one server-leased job a QZ signer is allowed to authorize. */
export interface PrintJobLeaseContext {
  jobId: string;
  leaseId: string;
}

export interface PrintRequest {
  documentHtml?: string;
  jobName: string;
  paperWidth: PaperWidth;
  copies: number;
  profile: TestedPrinterProfile;
  /** QZ/OS queue identity. Browser fallback does not select a queue. */
  queueName?: string;
  /**
   * A `QZ_NETWORK` printer: QZ sends a browser-rendered ESC/POS raster straight
   * to this address (docs §3a). `queueName` then carries the same `host:port`
   * so the signing intent stays keyed by the job's saved destination.
   */
  networkAddress?: { host: string; port: number };
  /** Required by QZ so signing can stay scoped to one leased durable job. */
  jobLease?: PrintJobLeaseContext;
  hardwareActions?: PrintHardwareActions;
}

export interface PrintDispatchOptions {
  /**
   * Durably enters SENDING. Providers call this only after every provider-side
   * fallible preparation step, immediately before the first actuation attempt.
   */
  onBeforeDispatch(): Promise<void>;
}

export interface PreparedPrint {
  provider: PrintProviderKind;
  /** May be called once. The provider owns the exact SENDING boundary. */
  send(options: PrintDispatchOptions): Promise<PrintEvidence>;
  /** Releases browser-only prepared resources when a job is cancelled. */
  dispose(): void;
}

export interface PrintProvider {
  kind: PrintProviderKind;
  /** Completes all deterministic/fallible preparation before send. */
  preflight(request: PrintRequest): Promise<PreparedPrint>;
}

export type PrintProviderErrorCode =
  | "INVALID_DOCUMENT"
  | "INVALID_COPIES"
  | "WIDTH_UNSUPPORTED"
  | "PIXEL_HTML_UNSUPPORTED"
  | "QUEUE_REQUIRED"
  | "QUEUE_NOT_FOUND"
  | "QZ_CONNECTION_FAILED"
  | "QZ_JOB_CONTEXT_REQUIRED"
  | "QZ_AUTHORIZATION_FAILED"
  | "QZ_DISPATCH_NOT_STARTED"
  | "BROWSER_PRINT_UNAVAILABLE"
  | "RAW_ACTION_UNSUPPORTED"
  | "ALREADY_SENT";

export class PrintProviderError extends Error {
  readonly name = "PrintProviderError";
  readonly code: PrintProviderErrorCode;
  readonly cause: unknown;

  constructor(
    code: PrintProviderErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.code = code;
    this.cause = cause;
  }
}
